import { useQuery, useMutation, keepPreviousData } from "@tanstack/react-query";
import { queryClient } from "@/shared/services/queryClient";
import {
  cropVarietiesApi,
  type CreateCropVarietyDto,
  type UpdateCropVarietyDto,
  type CropVarietyListQuery,
} from "../services/cropVarietiesApi";

export const useAllCropVarieties = () => {
  return useQuery({
    queryKey: ["cropVarieties"],
    queryFn: cropVarietiesApi.getAllCropVarieties,
  });
};

export const useCropVarietiesPaged = (query: CropVarietyListQuery = {}) => {
  return useQuery({
    queryKey: ["cropVarieties", "paged", query],
    queryFn: () => cropVarietiesApi.getCropVarietiesPaged(query),
    placeholderData: keepPreviousData,
  });
};

export const useActiveCropVarieties = () => {
  return useQuery({
    queryKey: ["cropVarieties", "active"],
    queryFn: cropVarietiesApi.getActiveCropVarieties,
  });
};

export const useCropVarietiesByCropType = (cropTypeId: string) => {
  return useQuery({
    queryKey: ["cropVarieties", "by-crop-type", cropTypeId],
    queryFn: () => cropVarietiesApi.getCropVarietiesByCropType(cropTypeId),
    enabled: !!cropTypeId,
  });
};

export const useCropVarietyById = (id: string) => {
  return useQuery({
    queryKey: ["cropVarieties", id],
    queryFn: () => cropVarietiesApi.getCropVarietyById(id),
    enabled: !!id,
  });
};

export const useCreateCropVariety = () => {
  return useMutation({
    mutationFn: (payload: CreateCropVarietyDto) =>
      cropVarietiesApi.createCropVariety(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cropVarieties"] });
    },
  });
};

export const useUpdateCropVariety = () => {
  return useMutation({
    mutationFn: (payload: UpdateCropVarietyDto) =>
      cropVarietiesApi.updateCropVariety(payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["cropVarieties"] });
      queryClient.invalidateQueries({ queryKey: ["cropVarieties", data.id] });
    },
  });
};

export const useDeleteCropVariety = () => {
  return useMutation({
    mutationFn: (id: string) => cropVarietiesApi.deleteCropVariety(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cropVarieties"] });
    },
  });
};
